import {
  Box,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { updateClientQuestions } from "../../../../apis/clients.api";
import { SaveAndCancelButton } from "../../../../components/buttons";
import { ChipTextField } from "../../../../components/chips/chip-textfield";
import FlexBox from "../../../../components/layouts/flexbox";
import MuiTabs from "../../../../components/mui/tabs";
import { ENUM_QUALIFY_QUESTION_TYPES } from "../../../../enums";
import {
  QUALIFY_QUESTION_TYPE_OPTIONS,
  qualifyQuestionFormConfig,
} from "./form-config";

type EditPreQualifyQuestionsProps = {
  questions?: Record<string, any>;
  onCancel: () => void;
  onSaved?: () => void;
};

function EditPreQualifyQuestions({
  questions,
  onCancel,
  onSaved,
}: EditPreQualifyQuestionsProps) {
  const dispatch = useDispatch();
  const { id: clientId }: any = useParams();
  const [formData, setFormData] = useState(
    qualifyQuestionFormConfig.setFormData(questions)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setFormData(qualifyQuestionFormConfig.setFormData(questions));
    setActiveStep(0);
  }, [questions]);

  const handleOnChange = (e: any, field: string, index: number) => {
    const next = [...formData];
    next[index] = {
      ...next[index],
      [field]: e.target.value,
    };

    if (field === "type") {
      next[index].error = {
        ...next[index].error,
        options:
          e.target.value === ENUM_QUALIFY_QUESTION_TYPES.MULTI_SELECT &&
          !next[index]?.options?.length
            ? "Options are required"
            : "",
      };
      if (e.target.value !== ENUM_QUALIFY_QUESTION_TYPES.MULTI_SELECT) {
        next[index].options = [];
      }
    }

    setFormData(next);
  };

  const handleOptionsChange = (e: any, index: number) => {
    const next = [...formData];
    next[index] = {
      ...next[index],
      options: e.target.value,
      error: {
        ...next[index].error,
        options: e.target.value?.length === 0 ? "Options are required" : "",
      },
    };
    setFormData(next);
  };

  const handleTabChange = (value: string) => {
    const nextIndex = Number(value);
    if (Number.isNaN(nextIndex)) return;
    setActiveStep(nextIndex);
  };

  const handleSave = async () => {
    const { isValid, formData: validated } =
      qualifyQuestionFormConfig.runValidation(formData);
    setFormData(validated);
    if (!isValid || !clientId) {
      const firstInvalid = validated.findIndex(
        (q) => q.error?.question || q.error?.options
      );
      if (firstInvalid >= 0) setActiveStep(firstInvalid);
      return;
    }

    const questionsPayload =
      qualifyQuestionFormConfig.generateQualifyingQuestionPayload(validated);

    setIsSaving(true);
    try {
      const res: any = await dispatch(
        updateClientQuestions(clientId, questionsPayload) as any
      );
      if (res?.success) {
        onSaved?.();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const q = formData[activeStep];
  if (!q) return null;

  const tabItems = formData.map((item, index) => ({
    value: String(index),
    label: `Question ${index + 1}`,
  }));

  return (
    <Box>
      <MuiTabs
        items={tabItems}
        selectedTab={String(activeStep)}
        onTabChange={handleTabChange}
        ariaLabel="Edit pre-qualify question tabs"
      />

      <Box pt={2.5} maxWidth={{ xs: "100%", md: 560 }}>
        <Typography fontSize={12} color="#94A3B8" mb={2}>
          Field key: {q.field}
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Typography fontSize={12} fontWeight={600} color="#475569" mb={0.75}>
              Answer type
            </Typography>
            <Select
              fullWidth
              size="small"
              value={q.type || ENUM_QUALIFY_QUESTION_TYPES.SHORT_TEXT}
              onChange={(e) => handleOnChange(e, "type", activeStep)}
              displayEmpty
            >
              {QUALIFY_QUESTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography fontSize={12} fontWeight={600} color="#475569" mb={0.75}>
              Question text *
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Enter a clear question for ambassadors"
              value={q.question || ""}
              onChange={(e) => handleOnChange(e, "question", activeStep)}
              error={Boolean(q?.error?.question)}
            />
            {q?.error?.question && (
              <FormHelperText error>{q.error.question}</FormHelperText>
            )}
          </Box>

          {q.type === ENUM_QUALIFY_QUESTION_TYPES.MULTI_SELECT && (
            <Box>
              <Typography
                fontSize={12}
                fontWeight={600}
                color="#475569"
                mb={0.75}
              >
                Options *
              </Typography>
              <ChipTextField
                id={`options-${q.field}`}
                field={q.field}
                value={q.options || []}
                onChange={(e) => handleOptionsChange(e, activeStep)}
                error={q?.error?.options}
                inlineLabel
                label="Type an option and press Enter"
              />
            </Box>
          )}
        </Stack>
      </Box>

      <FlexBox
        justifyContent={{ xs: "flex-end", md: "flex-start" }}
        mt={3}
        maxWidth={{ xs: "100%", md: 560 }}
      >
        <SaveAndCancelButton
          defaultText="Cancel"
          primaryText={isSaving ? "Saving..." : "Save"}
          onDefaultButtonClick={onCancel}
          onPrimaryActionClick={handleSave}
          isPrimaryButtonDisabled={isSaving}
        />
      </FlexBox>
    </Box>
  );
}

export { EditPreQualifyQuestions };
